"use client";

import React from "react";
import { LocaleLink as Link } from "@/components/shared/LocaleLink";
import { useTranslations } from "@/lib/i18n-client";
import { Reveal, Stagger, StaggerItem, Floating } from "@/components/shared/motion";
import { MapPin, QrCode, Wifi, ArrowRight } from "lucide-react";

/**
 * Coastal Freedom — home tail per generated_homepage_or_overview.png: "Get
 * Online in 3 Simple Steps" + an ocean CTA banner + a FAQ block. Client.
 */
export default function HomeTail() {
  const { t } = useTranslations();

  const steps = [
    { n: "1", icon: MapPin, title: t("cf.steps.s1", "Choose your plan"), body: t("cf.steps.s1b", "Pick a destination and the plan that suits your trip.") },
    { n: "2", icon: QrCode, title: t("cf.steps.s2", "Install eSIM"), body: t("cf.steps.s2b", "Scan the QR code and add the eSIM in seconds.") },
    { n: "3", icon: Wifi, title: t("cf.steps.s3", "Activate & connect"), body: t("cf.steps.s3b", "Turn on your eSIM and enjoy fast data wherever you go.") },
  ];

  const faqs = [
    { q: t("cf.faq.q1", "How much data do I need for my vacation?"), a: t("cf.faq.a1", "It depends how you travel: roughly 1–3GB for maps and chat, 5–10GB for social and photos, and 10GB+ for video calls and streaming. Our use-case selector helps you pick.") },
    { q: t("cf.faq.q2", "Is 'unlimited' data really unlimited?"), a: t("cf.faq.a2", "We lead with large, high-data plans and show the fair-use terms up front, so you know exactly what you get — no fine-print 'unlimited' surprises.") },
    { q: t("cf.faq.q3", "Can my family use the hotspot?"), a: t("cf.faq.a3", "Yes. Hotspot and tethering are supported, so couples and families can share one connection.") },
    { q: t("cf.faq.q4", "Can I keep my WhatsApp number?"), a: t("cf.faq.a4", "Yes. The eSIM is data-only and runs alongside your home SIM, so your number stays active.") },
  ];

  return (
    <section className="container mx-auto px-4 py-10">
      <div className="grid lg:grid-cols-3 gap-6 items-start">
        {/* steps */}
        <div>
          <div className="text-xs font-bold text-primary uppercase tracking-wide mb-2">{t("cf.steps.eyebrow", "How it works")}</div>
          <h2 className="text-2xl mb-5">{t("cf.steps.title", "Get Online in 3 Simple Steps")}</h2>
          <Stagger as="div" className="space-y-4" staggerChildren={0.13} amount={0.3}>
            {steps.map(({ n, icon: Icon, title, body }) => (
              <StaggerItem key={n} y={22} className="flex items-start gap-3">
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-primary text-primary-foreground font-bold shrink-0">{n}</span>
                <div><div className="inline-flex items-center gap-1.5 font-bold text-foreground text-sm"><Icon className="w-4 h-4 text-primary" />{title}</div><div className="text-xs text-muted-foreground">{body}</div></div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>

        {/* ocean CTA banner */}
        <Reveal as="div" y={24} className="relative rounded-2xl p-8 flex flex-col justify-center items-center text-center min-h-[220px] bg-gradient-to-br from-blue-300/30 via-cyan-200/20 to-sky-300/30 overflow-hidden">
          <span className="absolute inset-0 rounded-2xl bg-gradient-to-t from-primary/20 to-transparent" aria-hidden="true" />
          <Floating className="pointer-events-none absolute -top-6 -right-4 h-24 w-24 rounded-full bg-amber-200/40 blur-xl" aria-hidden="true">{null}</Floating>
          <div className="relative z-10 text-white">
            <h2 className="text-2xl font-extrabold drop-shadow">{t("cf.cta.title", "Ready to Travel Smarter?")}</h2>
            <p className="mt-2 text-white/90 drop-shadow text-sm max-w-xs mx-auto">{t("cf.cta.sub", "Stay connected wherever your journey takes you.")}</p>
            <Link href="/destinations/" className="mt-4 inline-flex items-center gap-2 rounded-full bg-white text-primary font-bold px-6 h-11 transition-all active:scale-[0.97] hover:-translate-y-0.5 hover:shadow-lg">{t("cf.cta.btn", "Explore eSIM plans")} <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </Reveal>

        {/* FAQ */}
        <div>
          <div className="text-xs font-bold text-primary uppercase tracking-wide mb-2">{t("cf.faq.eyebrow", "FAQs")}</div>
          <h2 className="text-2xl mb-4">{t("cf.faq.title", "Got Questions? We've Got Answers.")}</h2>
          <Stagger as="div" className="space-y-2" staggerChildren={0.08} amount={0.2}>
            {faqs.map(({ q, a }) => (
              <StaggerItem key={q} as="div" y={18}>
                <details className="group cf-card p-3">
                  <summary className="flex items-center justify-between cursor-pointer text-sm font-semibold text-foreground list-none">{q}<span className="text-primary group-open:rotate-45 transition-transform text-lg leading-none">+</span></summary>
                  <p className="mt-1.5 text-xs text-muted-foreground">{a}</p>
                </details>
              </StaggerItem>
            ))}
          </Stagger>
          <Link href="/contact-us/" className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-primary hover:underline">{t("cf.faq.all", "View all FAQs")} <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </div>
    </section>
  );
}
